import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cashReservesService from '../../services/cashReservesService';
import { fmtCurrencyPrecise, mergeSummaries } from './helpers';
import { getOrLoadTx, invalidateTxCache, readTxCache } from './txCache';

// Reserves available for linking from one account's configured reserves. Each
// option carries the reserve's stable id plus a human label (category, amount,
// and start date so same-category reserves stay distinguishable). When the
// reserve belongs to a different family account, the account name is included
// so fee payments funded from one account can target another account's reserve.
const reserveOptionsFor = (payload, { accountLabel = null } = {}) => [
  ...new Map(
    (payload?.reserve?.reserves || [])
      .filter((r) => r.reserve_id)
      .map((r) => [
        r.reserve_id,
        {
          id: r.reserve_id,
          label: [
            r.reserve_category || 'Uncategorized',
            accountLabel || null,
            r.calculated_reserve_dollar != null
              ? fmtCurrencyPrecise(r.calculated_reserve_dollar)
              : null,
            r.start_date ? `from ${r.start_date}` : null,
          ]
            .filter(Boolean)
            .join(' · '),
        },
      ])
  ).values(),
];

// Family-wide live reserves for the link picker. Same-family cross-account
// links are allowed (e.g. fee cash leaves account A against account B's
// management-fee reserve); outside the family stays out of the list.
const familyReserveOptionsFor = (accounts, byAccount, homeAccountNumber) => {
  const options = [];
  const seen = new Set();
  // Home account first so same-account choices stay at the top of the picker.
  const ordered = [
    ...accounts.filter((a) => a.account_number === homeAccountNumber),
    ...accounts.filter((a) => a.account_number !== homeAccountNumber),
  ];
  ordered.forEach((account) => {
    const number = account.account_number;
    const isHome = number === homeAccountNumber;
    const accountLabel = isHome ? null : account.account_name || number;
    reserveOptionsFor(byAccount[number]?.payload, { accountLabel }).forEach((option) => {
      if (seen.has(option.id)) return;
      seen.add(option.id);
      options.push(option);
    });
  });
  return options;
};

/**
 * Reserves that have left Tamarac but remain valid link targets.
 *
 * A withdrawal is often the very thing that drained a reserve before it was
 * deleted, so recording that link is the point. Two carve-outs:
 *  - already combined into a successor: link to the successor instead, which
 *    is what alerting resolves the retired id to anyway;
 *  - a fingerprint that merely churned because the reserve was EDITED. Linking
 *    to one of those marks the withdrawal fully explained and silences the
 *    remove/lower alerts the live successor should raise, so it's flagged
 *    rather than hidden — Combine is the right tool there.
 */
const retiredOptionsFor = (lineage) => {
  const suggested = new Set(
    (lineage?.suggestions || []).map((s) => s.retired_reserve_id)
  );
  return (lineage?.retired || [])
    .filter((r) => r.reserve_id && !r.linked_to)
    .map((r) => {
      // retired[] carries the RAW report amount, not a dollar figure — a
      // percent-of-account reserve of 2.5 must not render as $2.50.
      const isPercent = /percent|%/i.test(String(r.reserve_value_type ?? ''));
      const amount =
        r.reserve_amount == null
          ? null
          : isPercent
          ? `${r.reserve_amount}%`
          : fmtCurrencyPrecise(r.reserve_amount);
      return {
        id: r.reserve_id,
        label: [
          r.reserve_category || 'Uncategorized',
          amount,
          r.retired_on ? `retired ${r.retired_on}` : 'retired',
          suggested.has(r.reserve_id) ? 'likely an edit — consider Combine' : null,
        ]
          .filter(Boolean)
          .join(' · '),
      };
    });
};

/**
 * Quarterly management-fee snapshots — frozen copies of the Management Fee
 * reserve, one per completed quarter.
 *
 * Tamarac carries a single Management Fee reserve whose value tracks the
 * portfolio, so linking a fee withdrawal to it says nothing about which quarter
 * was paid. Linking to one of these does. Always dollars — the backend
 * normalizes a percent-of-account fee against the quarter-end account value
 * before freezing it, so unlike retiredOptionsFor there is no raw-amount trap.
 */
const snapshotOptionsFor = (lineage) =>
  (lineage?.fee_snapshots || [])
    .filter((s) => s.reserve_id)
    .map((s) => ({
      id: s.reserve_id,
      label: [
        // Already carries its quarter, e.g. "Management Fee — Q1 2026".
        s.reserve_category || 'Uncategorized',
        s.reserve_amount != null ? fmtCurrencyPrecise(s.reserve_amount) : null,
        (s.linked_transactions || []).length > 0 ? 'already linked' : null,
      ]
        .filter(Boolean)
        .join(' · '),
    }));

const OUTFLOW_REASON_CATEGORIES = [
  {
    value: 'Cash Need',
    examples: [
      'One-time cash need',
      'Monthly cash need/living expenses',
      'Annual spending need',
    ],
  },
  {
    value: 'Retirement Distributions',
    examples: [
      'RMD to bank',
      'RMD journaled to taxable account',
    ],
  },
  {
    value: 'Taxes',
    examples: ['Tax liability payment'],
  },
  {
    value: 'Debt',
    examples: [
      'Mortgage payoff/paydown',
      'Loan payoff/paydown',
      'Credit card payoff',
    ],
  },
  {
    value: 'Real Estate',
    examples: [
      'Home purchase/down payment',
      'Land purchase',
      'Home improvement/repair',
      'Condo assessment',
    ],
  },
  {
    value: 'Major Purchases',
    examples: ['New auto purchase', 'Big purchase', 'Big trip'],
  },
  {
    value: 'Gifting & Charitable',
    examples: ['Gift to family', 'Charitable gift', 'Qualified Charitable Distribution (QCD)'],
  },
  {
    // The outflow half of the inflow list's 'Transfers' — money leaving for
    // another account rather than being spent. Named in the singular so the
    // two never read as the same category when a transaction's flow can't be
    // inferred and both lists are offered together.
    value: 'Transfer',
    examples: [
      'Transfer to another account',
      'Transfer to an outside custodian (ACAT out)',
      'Rollover out',
    ],
  },
];

const INFLOW_REASON_CATEGORIES = [
  {
    value: 'Savings',
    examples: ['Regular savings', 'One-time contribution'],
  },
  {
    value: 'Retirement Contributions',
    examples: ['Annual contribution (IRA/Roth)', 'Prior-year contribution'],
  },
  {
    value: 'Transfers',
    examples: ['Account transfer', 'Rollover'],
  },
  {
    value: 'Proceeds/Windfalls',
    examples: ['Sale proceeds', 'Business proceeds', 'Inheritance/gift received'],
  },
];

const inferTransactionFlow = (tx) => {
  const type = String(tx?.activity_type || '').trim().toLowerCase();
  const amount = Number(tx?.net_amount ?? tx?.amount ?? 0) || 0;
  if (
    type.includes('withdraw') ||
    type.includes('expense') ||
    type.includes('debit') ||
    type.includes('distribution') ||
    amount < 0
  ) {
    return 'outflow';
  }
  if (
    type.includes('deposit') ||
    type.includes('contribution') ||
    type.includes('credit') ||
    type.includes('rollover') ||
    amount > 0
  ) {
    return 'inflow';
  }
  return null;
};

const reasonCategoriesForTransaction = (tx) => {
  const flow = inferTransactionFlow(tx);
  if (flow === 'outflow') return OUTFLOW_REASON_CATEGORIES;
  if (flow === 'inflow') return INFLOW_REASON_CATEGORIES;
  return [...OUTFLOW_REASON_CATEGORIES, ...INFLOW_REASON_CATEGORIES];
};

const DEFAULT_SORT = { key: 'tradeDate', direction: 'desc' };

const FIRST_DIRECTION_BY_KEY = {
  tradeDate: 'desc',
  account: 'asc',
  activityType: 'asc',
  symbol: 'asc',
  amount: 'desc',
  note: 'asc',
};

/**
 * Transactions for one or more accounts, merged into a single date-ordered table.
 *
 * Fetching stays per account (that's the shape of the endpoint), so results are
 * requested in parallel and written to state as each resolves — a slow account
 * doesn't hold up the rest, and a failed one gets an inline retry instead of
 * blanking the table.
 *
 * `accounts` is the FETCH scope — always the whole family — while
 * `visibleAccounts` is the RENDER scope. Keeping them separate is what lets the
 * user filter the panel without re-running the fetch effect: narrowing can't
 * save any requests anyway (the family is fetched when the panel opens), and
 * refetching would discard a failed account's retained error and could flash
 * the whole table back to a spinner on a checkbox click.
 */
const TransactionsPanel = ({
  accounts,
  visibleAccounts,
  days,
  requestOptions,
  cacheScope,
  activityTypeFilter = '',
  minAbsoluteAmount = '',
  hideSummaryStrip = false,
  onRowsChanged,
  reloadToken = 0,
  onAnnotationChanged,
}) => {
  // The accounts array is rebuilt on every filter/sort/reload upstream, so
  // effects depend on this string rather than the array's identity — otherwise
  // they refetch forever.
  const accountKey = accounts.map((a) => a.account_number).join(',');
  const shown = visibleAccounts || accounts;
  const visibleKey = shown.map((a) => a.account_number).join(',');
  const showAccount = shown.length > 1;
  const txOptions = useMemo(() => ({ days, ...(requestOptions || {}) }), [days, requestOptions]);
  const resolvedCacheScope =
    cacheScope != null && cacheScope !== ''
      ? String(cacheScope)
      : `${txOptions.days}|${txOptions.as_of || 'latest'}`;
  const normalizedTypeFilters = useMemo(
    () =>
      Array.isArray(activityTypeFilter)
        ? activityTypeFilter
            .map((value) => String(value || '').trim().toLowerCase())
            .filter(Boolean)
        : String(activityTypeFilter || '').trim()
          ? [String(activityTypeFilter || '').trim().toLowerCase()]
          : [],
    [activityTypeFilter]
  );
  const parsedMinAmount = Number.parseFloat(String(minAbsoluteAmount ?? '').trim());
  const minThreshold = Number.isFinite(parsedMinAmount) && parsedMinAmount > 0 ? parsedMinAmount : 0;

  const [byAccount, setByAccount] = useState(() => {
    const seed = {};
    accounts.forEach((a) => {
      const cached = readTxCache(a.account_number, resolvedCacheScope);
      if (cached) seed[a.account_number] = { payload: cached };
    });
    return seed;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);

  // Editing is keyed by account + transaction, never transaction alone:
  // annotations are stored per (account_number, transaction_id) and Tamarac ids
  // are not unique across accounts.
  const [editingKey, setEditingKey] = useState(null);
  // Retired reserves per account, fetched only when an editor is opened —
  // opening one is a deliberate, occasional action, whereas this panel is
  // rendered for every account of every expanded family.
  const [lineageByAccount, setLineageByAccount] = useState({});
  const [draftNote, setDraftNote] = useState('');
  const [draftReasonCategory, setDraftReasonCategory] = useState('');
  const [draftLinks, setDraftLinks] = useState([]);
  const [savingNote, setSavingNote] = useState(false);
  const [noteError, setNoteError] = useState('');
  // Clearing is keyed by row, unlike savingNote which can be a single boolean
  // because only one editor is open at a time. The clear button sits on the row
  // itself, so it can be used on a transaction whose editor was never opened —
  // and for the same reason its failure has nowhere to land but the row, since
  // noteError renders inside an editor that won't exist here.
  const [clearingKey, setClearingKey] = useState(null);
  const [clearError, setClearError] = useState(null); // { key, message }

  const requestIdRef = useRef(0);
  const loadedScopeRef = useRef(resolvedCacheScope);
  const linkKeyRef = useRef(0);
  const nextLinkKey = () => `link-${(linkKeyRef.current += 1)}`;

  const fetchAccount = useCallback(
    async (accountNumber, isCurrent) => {
      try {
        // Shared with any concurrent mount (Strict Mode remount, twin panels):
        // one HTTP call per account/window, written to cache when it resolves.
        const result = await getOrLoadTx(accountNumber, resolvedCacheScope, () =>
          cashReservesService.getAccountTransactions(accountNumber, txOptions)
        );
        if (!isCurrent()) return;
        setByAccount((prev) => ({ ...prev, [accountNumber]: { payload: result } }));
      } catch (err) {
        if (!isCurrent()) return;
        setByAccount((prev) => ({
          ...prev,
          [accountNumber]: {
            error: err.response?.data?.detail || err.message || 'Failed to load transactions',
          },
        }));
      }
    },
    [resolvedCacheScope, txOptions]
  );

  const load = useCallback(async () => {
    const requestId = (requestIdRef.current += 1);
    const isCurrent = () => requestId === requestIdRef.current;
    const numbers = accountKey ? accountKey.split(',') : [];

    const misses = [];
    const seeded = {};
    numbers.forEach((n) => {
      const cached = readTxCache(n, resolvedCacheScope);
      if (cached) seeded[n] = { payload: cached };
      else misses.push(n);
    });
    // Serve what the cache already has, then fill the gaps.
    setByAccount((prev) => {
      const next = {};
      numbers.forEach((n) => {
        next[n] = seeded[n] || prev[n];
      });
      return next;
    });

    if (misses.length === 0) {
      loadedScopeRef.current = resolvedCacheScope;
      setIsLoading(false);
      return;
    }
    loadedScopeRef.current = '';
    setIsLoading(true);
    await Promise.allSettled(misses.map((n) => fetchAccount(n, isCurrent)));
    if (isCurrent()) {
      loadedScopeRef.current = resolvedCacheScope;
      setIsLoading(false);
    }
  }, [accountKey, resolvedCacheScope, fetchAccount]);

  // reloadToken means "re-evaluate the cache", not "refetch everything": callers
  // invalidate the one account they changed, so the rest still come from cache.
  useEffect(() => {
    load();
    return () => {
      requestIdRef.current += 1;
    };
  }, [load, reloadToken]);

  const retry = (accountNumber) => {
    const requestId = requestIdRef.current;
    invalidateTxCache(accountNumber);
    setByAccount((prev) => ({ ...prev, [accountNumber]: undefined }));
    fetchAccount(accountNumber, () => requestId === requestIdRef.current);
  };

  // An editor open on an account that just got filtered out would keep its
  // draft and re-open with it when the account comes back.
  useEffect(() => {
    setEditingKey(null);
    setNoteError('');
  }, [visibleKey]);

  const rows = useMemo(() => {
    const merged = shown.flatMap((a, ai) =>
      (byAccount[a.account_number]?.payload?.transactions || []).map((tx, i) => ({
        tx,
        accountNumber: a.account_number,
        accountName: a.account_name,
        ai,
        i,
        // The backend serves trade_date from either the history store or the
        // live report, so parse rather than compare the strings.
        ts: Date.parse(tx.trade_date),
      }))
    );
    return merged;
  }, [shown, byAccount]);

  const sortedRows = useMemo(() => {
    const directionSign = sortBy.direction === 'asc' ? 1 : -1;
    const sorted = [...rows];
    sorted.sort((x, y) => {
      const tieBreak = () => x.ai - y.ai || x.i - y.i;

      if (sortBy.key === 'tradeDate') {
        const xInvalid = Number.isNaN(x.ts);
        const yInvalid = Number.isNaN(y.ts);
        if (xInvalid !== yInvalid) return xInvalid ? 1 : -1;
        if (!xInvalid && x.ts !== y.ts) return (x.ts - y.ts) * directionSign;
        return tieBreak();
      }

      if (sortBy.key === 'amount') {
        const xAmount = Number(x.tx.net_amount ?? x.tx.amount ?? 0) || 0;
        const yAmount = Number(y.tx.net_amount ?? y.tx.amount ?? 0) || 0;
        if (xAmount !== yAmount) return (xAmount - yAmount) * directionSign;
        return tieBreak();
      }

      if (sortBy.key === 'account') {
        const xAccount = String(x.accountName || x.accountNumber || '');
        const yAccount = String(y.accountName || y.accountNumber || '');
        const cmp = xAccount.localeCompare(yAccount, undefined, {
          sensitivity: 'base',
          numeric: true,
        });
        if (cmp !== 0) return cmp * directionSign;
        return tieBreak();
      }

      if (sortBy.key === 'activityType') {
        const xType = String(x.tx.activity_type || '');
        const yType = String(y.tx.activity_type || '');
        const cmp = xType.localeCompare(yType, undefined, {
          sensitivity: 'base',
          numeric: true,
        });
        if (cmp !== 0) return cmp * directionSign;
        return tieBreak();
      }

      if (sortBy.key === 'symbol') {
        const xSymbol = String(x.tx.symbol || '');
        const ySymbol = String(y.tx.symbol || '');
        const cmp = xSymbol.localeCompare(ySymbol, undefined, {
          sensitivity: 'base',
          numeric: true,
        });
        if (cmp !== 0) return cmp * directionSign;
        return tieBreak();
      }

      if (sortBy.key === 'note') {
        const xHasNote = Boolean(
          x.tx.user_note || x.tx.user_note_reason_category || (x.tx.linked_reserves || []).length
        );
        const yHasNote = Boolean(
          y.tx.user_note || y.tx.user_note_reason_category || (y.tx.linked_reserves || []).length
        );
        if (xHasNote !== yHasNote) return (xHasNote ? 1 : -1) * directionSign;

        const xNote = String(x.tx.user_note_reason_category || x.tx.user_note || '');
        const yNote = String(y.tx.user_note_reason_category || y.tx.user_note || '');
        const cmp = xNote.localeCompare(yNote, undefined, {
          sensitivity: 'base',
          numeric: true,
        });
        if (cmp !== 0) return cmp * directionSign;

        const xLinks = (x.tx.linked_reserves || []).length;
        const yLinks = (y.tx.linked_reserves || []).length;
        if (xLinks !== yLinks) return (xLinks - yLinks) * directionSign;
        return tieBreak();
      }

      return tieBreak();
    });
    return sorted;
  }, [rows, sortBy]);

  const summary = useMemo(
    () =>
      mergeSummaries(
        shown
          .map((a) => byAccount[a.account_number]?.payload?.transaction_summary)
          .filter(Boolean)
      ),
    [shown, byAccount]
  );

  const filteredRows = useMemo(() => {
    if (normalizedTypeFilters.length === 0 && minThreshold <= 0) return sortedRows;
    return sortedRows.filter((row) => {
      const rowType = String(row.tx.activity_type || '').trim().toLowerCase();
      if (normalizedTypeFilters.length > 0 && !normalizedTypeFilters.includes(rowType)) return false;
      const impact = row.tx.net_amount ?? row.tx.amount ?? 0;
      if (minThreshold > 0 && Math.abs(Number(impact) || 0) < minThreshold) return false;
      return true;
    });
  }, [sortedRows, normalizedTypeFilters, minThreshold]);

  const filteredSummary = useMemo(() => {
    if (filteredRows.length === rows.length) return summary;
    const aggregate = filteredRows.reduce(
      (acc, row) => {
        const impact = Number(row.tx.net_amount ?? row.tx.amount ?? 0) || 0;
        if (impact >= 0) acc.inflow += impact;
        else acc.outflow += impact;
        acc.transaction_count += 1;
        acc.net_cash_impact += impact;
        return acc;
      },
      { transaction_count: 0, inflow: 0, outflow: 0, net_cash_impact: 0 }
    );
    return aggregate;
  }, [filteredRows, rows.length, summary]);

  const activityTypes = useMemo(
    () =>
      [...new Set(sortedRows.map((row) => row.tx.activity_type).filter(Boolean))].sort((a, b) =>
        String(a).localeCompare(String(b), undefined, { sensitivity: 'base' })
      ),
    [sortedRows]
  );

  useEffect(() => {
    if (!onRowsChanged) return;
    onRowsChanged({
      rows: sortedRows,
      filteredRows,
      activityTypes,
      summary: filteredSummary,
      loadedCacheScope: loadedScopeRef.current,
    });
  }, [onRowsChanged, sortedRows, filteredRows, activityTypes, filteredSummary]);

  const toggleSort = useCallback((key) => {
    setSortBy((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: FIRST_DIRECTION_BY_KEY[key] || 'asc' };
    });
  }, []);

  const sortIconFor = (key) => {
    if (sortBy.key !== key) return 'bi-arrow-down-up text-muted';
    return sortBy.direction === 'asc' ? 'bi-sort-up' : 'bi-sort-down';
  };

  const ariaSortFor = (key) => {
    if (sortBy.key !== key) return 'none';
    return sortBy.direction === 'asc' ? 'ascending' : 'descending';
  };

  // Scoped to what's shown: a hidden account's failure shouldn't mark these
  // totals partial, because they genuinely are complete for the visible set.
  const failed = shown.filter((a) => byAccount[a.account_number]?.error);
  const colSpan = showAccount ? 6 : 5;

  const openEditor = (row) => {
    // Load this account's retired reserves once; the picker fills them in when
    // they land, so the editor opens immediately on the current ones.
    if (lineageByAccount[row.accountNumber] === undefined) {
      setLineageByAccount((prev) => ({ ...prev, [row.accountNumber]: null }));
      cashReservesService
        .getReserveLineage(row.accountNumber)
        .then((result) =>
          setLineageByAccount((prev) => ({ ...prev, [row.accountNumber]: result }))
        )
        .catch(() => {
          // Non-fatal: the editor still works with the current reserves.
          setLineageByAccount((prev) => ({ ...prev, [row.accountNumber]: null }));
        });
    }
    setEditingKey(`${row.accountNumber}|${row.tx.transaction_id}`);
    setDraftNote(row.tx.user_note || '');
    setDraftReasonCategory(row.tx.user_note_reason_category || '');
    const existingLinks = row.tx.linked_reserves || [];
    setDraftLinks(
      existingLinks.length > 0
        ? existingLinks.map((link) => ({
            _key: nextLinkKey(),
            reserve_id: link.reserve_id || '',
            allocated_amount: link.allocated_amount != null ? String(link.allocated_amount) : '',
            label: link.reserve_label || '',
          }))
        : // No annotation yet: pre-fill the amount with the full transaction
          // value, since a single reserve usually covers it entirely.
          [
            {
              _key: nextLinkKey(),
              reserve_id: '',
              allocated_amount: Math.abs(
                Number(row.tx.net_amount ?? row.tx.amount ?? 0) || 0
              ).toFixed(2),
              label: '',
            },
          ]
    );
    setNoteError('');
  };

  const closeEditor = () => {
    setEditingKey(null);
    setDraftReasonCategory('');
    setNoteError('');
  };

  const addDraftLink = () =>
    setDraftLinks((prev) => [
      ...prev,
      { _key: nextLinkKey(), reserve_id: '', allocated_amount: '', label: '' },
    ]);

  const updateDraftLink = (index, patch) =>
    setDraftLinks((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const removeDraftLink = (index) =>
    setDraftLinks((prev) => prev.filter((_, i) => i !== index));

  const upsertPrimaryLink = (patch) => {
    setDraftLinks((prev) => {
      if (prev.length === 0) {
        return [
          {
            _key: nextLinkKey(),
            reserve_id: '',
            allocated_amount: '',
            label: '',
            ...patch,
          },
        ];
      }
      return prev.map((row, index) => (index === 0 ? { ...row, ...patch } : row));
    });
  };

  const saveNote = async (row) => {
    const { tx, accountNumber } = row;
    const note = draftNote.trim();
    const noteReasonCategory = String(draftReasonCategory || '').trim();
    if (noteReasonCategory && !note) {
      setNoteError('Reason category requires note text.');
      return;
    }
    // Rows without a selected reserve are ignored; blank amounts become null.
    const links = draftLinks
      .filter((r) => r.reserve_id)
      .map((r) => {
        const amount = String(r.allocated_amount ?? '').trim();
        return {
          reserve_id: r.reserve_id,
          allocated_amount: amount === '' ? null : Number(amount),
        };
      });
    if (
      links.some(
        (l) => l.allocated_amount != null && (!Number.isFinite(l.allocated_amount) || l.allocated_amount < 0)
      )
    ) {
      setNoteError('Allocated amounts must be finite, non-negative numbers.');
      return;
    }
    const hadAnnotation = Boolean(
      tx.user_note || tx.user_note_reason_category || (tx.linked_reserves || []).length
    );
    setSavingNote(true);
    setNoteError('');
    try {
      if (!note && links.length === 0) {
        // Clearing everything on an annotated transaction removes the annotation.
        if (hadAnnotation) {
          await cashReservesService.deleteTransactionNote(accountNumber, tx.transaction_id);
        }
      } else {
        await cashReservesService.saveTransactionNote(accountNumber, tx.transaction_id, {
          note,
          note_reason_category: noteReasonCategory || null,
          links,
        });
      }
      // Only this account's copy went stale. Refreshed here rather than via the
      // parent so the Note column can't be broken by how this panel is wired.
      invalidateTxCache(accountNumber);
      const requestId = requestIdRef.current;
      await fetchAccount(accountNumber, () => requestId === requestIdRef.current);
      closeEditor();
      // Alerts are computed server-side from the annotations, so only a reload
      // of the accounts payload can clear (or restore) a suggested action.
      if (onAnnotationChanged) onAnnotationChanged(accountNumber);
    } catch (err) {
      setNoteError(err.response?.data?.detail || err.message || 'Failed to save the note');
    } finally {
      setSavingNote(false);
    }
  };

  /** Name what's about to be destroyed — links can carry typed amounts too. */
  const clearConfirmMessage = (tx) => {
    const linkCount = (tx.linked_reserves || []).length;
    const parts = [];
    if (tx.user_note) parts.push('the note');
    if (tx.user_note_reason_category) parts.push('the reason category');
    if (linkCount) parts.push(`${linkCount} reserve link${linkCount === 1 ? '' : 's'}`);
    return `Remove ${parts.join(' and ')} from this transaction? This cannot be undone.`;
  };

  // One-click removal of a whole annotation, replacing the open-editor /
  // delete-the-text / remove-each-link / save round trip. The housekeeping
  // deliberately mirrors saveNote's: only this account's cached copy went
  // stale, and onAnnotationChanged is what reloads the server-computed
  // Suggested Actions and flips the fee-snapshot badge back to Not linked.
  const clearAnnotation = async (row) => {
    const { tx, accountNumber } = row;
    if (tx.transaction_id == null) return;
    const key = `${accountNumber}|${tx.transaction_id}`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(clearConfirmMessage(tx))) return;
    setClearingKey(key);
    setClearError(null);
    try {
      await cashReservesService.deleteTransactionNote(accountNumber, tx.transaction_id);
      invalidateTxCache(accountNumber);
      const requestId = requestIdRef.current;
      await fetchAccount(accountNumber, () => requestId === requestIdRef.current);
      // An editor open on this row would still be holding drafts of links that
      // no longer exist.
      if (editingKey === key) closeEditor();
      if (onAnnotationChanged) onAnnotationChanged(accountNumber);
    } catch (err) {
      setClearError({
        key,
        message: err.response?.data?.detail || err.message || 'Failed to clear the annotation',
      });
    } finally {
      setClearingKey(null);
    }
  };

  if (isLoading && rows.length === 0) {
    return (
      <div className="text-center py-3 text-muted">
        <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
        Loading transactions…
      </div>
    );
  }

  const failures = failed.map((a) => (
    <div key={a.account_number} className="alert alert-danger py-1 px-2 small mb-2" role="alert">
      <i className="bi bi-exclamation-triangle-fill me-2"></i>
      Could not load transactions for {a.account_number} — {byAccount[a.account_number].error}
      <button
        type="button"
        className="btn btn-sm btn-link p-0 ms-2 text-decoration-none"
        onClick={() => retry(a.account_number)}
      >
        Retry
      </button>
    </div>
  ));

  if (rows.length === 0) {
    return (
      <div>
        {failures}
        <div className="text-center py-3 text-muted">
          <i className="bi bi-inbox me-2"></i>
          No transactions in the last {days} days.
        </div>
      </div>
    );
  }

  return (
    <div>
      {failures}
      {/* Cash-flow summary strip */}
      {!hideSummaryStrip && (
        <div className="d-flex flex-wrap gap-3 mb-3 p-2 rounded" style={{ background: '#eef1f6', fontSize: '0.82rem' }}>
          <div><span className="text-muted">Transactions:</span> <span className="fw-semibold">{filteredSummary.transaction_count}</span></div>
          <div><span className="text-muted">Inflow:</span> <span className="fw-semibold text-success">{fmtCurrencyPrecise(filteredSummary.inflow)}</span></div>
          <div><span className="text-muted">Outflow:</span> <span className="fw-semibold text-danger">{fmtCurrencyPrecise(filteredSummary.outflow)}</span></div>
          <div>
            <span className="text-muted">Net:</span>{' '}
            <span className={`fw-semibold ${(filteredSummary.net_cash_impact ?? 0) >= 0 ? 'text-success' : 'text-danger'}`}>
              {fmtCurrencyPrecise(filteredSummary.net_cash_impact)}
            </span>
          </div>
          {filteredRows.length === rows.length && summary.unclassified_activity_types?.length > 0 && (
            <div className="text-warning">
              <i className="bi bi-question-circle me-1"></i>
              Unclassified: {summary.unclassified_activity_types.join(', ')}
            </div>
          )}
          {failed.length > 0 && (
            <div className="text-danger" title="Some accounts could not be loaded, so these totals are incomplete">
              (partial)
            </div>
          )}
        </div>
      )}

      {filteredRows.length === 0 && rows.length > 0 && (
        <div className="text-center py-3 text-muted">
          <i className="bi bi-funnel me-2"></i>
          No transactions match the selected filters.
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-sm align-middle mb-0 bg-white">
          <thead>
            <tr>
              <th aria-sort={ariaSortFor('tradeDate')}>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-decoration-none text-dark fw-semibold d-inline-flex align-items-center gap-1"
                  onClick={() => toggleSort('tradeDate')}
                >
                  Trade Date
                  <i className={`bi ${sortIconFor('tradeDate')}`}></i>
                </button>
              </th>
              {/* Floor the column so short names aren't squeezed narrow — the
                  Note column below is pinned at 35%, so this one competes for
                  what's left. It still grows for longer names and wraps. */}
              {showAccount && (
                <th aria-sort={ariaSortFor('account')} style={{ minWidth: '12rem' }}>
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 text-decoration-none text-dark fw-semibold d-inline-flex align-items-center gap-1"
                    onClick={() => toggleSort('account')}
                  >
                    Account
                    <i className={`bi ${sortIconFor('account')}`}></i>
                  </button>
                </th>
              )}
              <th aria-sort={ariaSortFor('activityType')}>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-decoration-none text-dark fw-semibold d-inline-flex align-items-center gap-1"
                  onClick={() => toggleSort('activityType')}
                >
                  Activity Type
                  <i className={`bi ${sortIconFor('activityType')}`}></i>
                </button>
              </th>
              <th aria-sort={ariaSortFor('symbol')}>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-decoration-none text-dark fw-semibold d-inline-flex align-items-center gap-1"
                  onClick={() => toggleSort('symbol')}
                >
                  Symbol
                  <i className={`bi ${sortIconFor('symbol')}`}></i>
                </button>
              </th>
              <th className="text-end" aria-sort={ariaSortFor('amount')}>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-decoration-none text-dark fw-semibold d-inline-flex align-items-center justify-content-end gap-1 w-100"
                  onClick={() => toggleSort('amount')}
                >
                  Amount
                  <i className={`bi ${sortIconFor('amount')}`}></i>
                </button>
              </th>
              <th className="text-center" style={{ width: '35%' }} aria-sort={ariaSortFor('note')}>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-decoration-none text-dark fw-semibold d-inline-flex align-items-center justify-content-center gap-1"
                  onClick={() => toggleSort('note')}
                >
                  Note
                  <i className={`bi ${sortIconFor('note')}`}></i>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row) => {
              const { tx, accountNumber, accountName } = row;
              const impact = tx.net_amount ?? tx.amount;
              const rowKey = `${accountNumber}|${
                tx.transaction_id ?? `${tx.trade_date}-${tx.activity_type}-${impact}-${row.i}`
              }`;
              // Annotations are stored per (account, transaction), so this is
              // the key both the editor and the clear button work from.
              // Deliberately not rowKey, which carries a synthetic fallback for
              // transactions with no id.
              const annotationKey =
                tx.transaction_id != null ? `${accountNumber}|${tx.transaction_id}` : null;
              const editing = annotationKey !== null && editingKey === annotationKey;
              const linkedReserves = tx.linked_reserves || [];
              const reasonCategory = tx.user_note_reason_category || '';
              const hasNote = Boolean(tx.user_note || reasonCategory || linkedReserves.length);
              const reasonOptions = reasonCategoriesForTransaction(tx);
              const selectedReasonOption = reasonOptions.find(
                (option) => option.value === draftReasonCategory
              );
              // Live reserves may come from any account in the family (fees are
              // often paid from one account against another's fee reserve).
              // Retired reserves and fee snapshots stay home-account only —
              // those ids are account-scoped history artifacts.
              const reserveOptions = [
                ...familyReserveOptionsFor(accounts, byAccount, accountNumber),
                ...retiredOptionsFor(lineageByAccount[accountNumber]),
                // Snapshot ids can't collide with the two lists above: the
                // backend keeps them out of `retired` precisely so they're
                // never Combine targets, and they were never live reserves.
                ...snapshotOptionsFor(lineageByAccount[accountNumber]),
              ];
              const inputId = `txNote-${accountNumber}-${tx.transaction_id}`;
              return (
                <React.Fragment key={rowKey}>
                  <tr>
                    <td>{tx.trade_date || '—'}</td>
                    {showAccount && (
                      <td title={`${accountName} · ${accountNumber}`}>
                        {accountName || accountNumber}
                      </td>
                    )}
                    <td>{tx.activity_type || '—'}</td>
                    <td>{tx.symbol || <span className="text-muted">—</span>}</td>
                    <td className="text-end">{fmtCurrencyPrecise(impact)}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <div className="flex-grow-1 text-center" style={{ minWidth: 0 }}>
                          {linkedReserves.map((link) => (
                            <span
                              key={link.reserve_id}
                              className="badge bg-info-subtle text-info-emphasis border me-1"
                              title={`Linked to the ${link.reserve_label || link.reserve_id} reserve`}
                            >
                              <i className="bi bi-link-45deg me-1"></i>
                              {link.reserve_label || link.reserve_id}
                              {link.allocated_amount != null && (
                                <> · {fmtCurrencyPrecise(link.allocated_amount)}</>
                              )}
                            </span>
                          ))}
                          {(tx.user_note || reasonCategory) && (
                            <span
                              className="small"
                              title={tx.note_updated_by ? `Note by ${tx.note_updated_by}` : undefined}
                            >
                              {reasonCategory && (
                                <span className="badge bg-secondary-subtle text-secondary-emphasis border me-1">
                                  {reasonCategory}
                                </span>
                              )}
                              {tx.user_note || <span className="text-muted">No free-form note</span>}
                            </span>
                          )}
                          {!hasNote && (
                            // Empty state: a subtle line spanning the full column width.
                            <span
                              className="d-block w-100 border-top"
                              style={{ opacity: 0.5 }}
                              aria-hidden="true"
                            ></span>
                          )}
                        </div>
                        {tx.transaction_id != null && (
                          <button
                            type="button"
                            className="btn btn-sm btn-link text-secondary p-0"
                            onClick={() => (editing ? closeEditor() : openEditor(row))}
                            // Locked while this row's clear is in flight:
                            // openEditor seeds its drafts from an annotation
                            // that is mid-deletion, and the clear's own
                            // closeEditor would then throw away whatever was
                            // typed. Safe against the initial clearingKey of
                            // null because this button only renders when
                            // transaction_id — and so annotationKey — is set.
                            disabled={clearingKey === annotationKey}
                            title={hasNote ? 'Edit note' : 'Add a note / link a reserve'}
                            aria-label={hasNote ? 'Edit note' : 'Add a note or link a reserve'}
                            aria-expanded={editing}
                          >
                            <i className={`bi ${hasNote ? 'bi-pencil-square' : 'bi-plus-circle'}`}></i>
                          </button>
                        )}
                        {annotationKey && hasNote && (
                          // text-secondary rather than text-danger: a column of
                          // red X's down every annotated row reads as an error
                          // state, and the confirm dialog is the real guard.
                          <button
                            type="button"
                            className="btn btn-sm btn-link text-secondary p-0"
                            onClick={() => clearAnnotation(row)}
                            disabled={clearingKey === annotationKey}
                            title="Clear this transaction's note and reserve links"
                            aria-label="Clear note and reserve links"
                          >
                            <i
                              className={`bi ${
                                clearingKey === annotationKey
                                  ? 'bi-hourglass-split'
                                  : 'bi-x-circle'
                              }`}
                            ></i>
                          </button>
                        )}
                      </div>
                      {clearError?.key === annotationKey && (
                        <div className="text-danger small mt-1">{clearError.message}</div>
                      )}
                    </td>
                  </tr>
                  {editing && (
                    <tr>
                      <td colSpan={colSpan} style={{ background: '#f1f3f7' }}>
                        <div className="p-2">
                          <div className="mb-2 w-100">
                            <div className="row g-2 align-items-end">
                              <div className="col-xl-2 col-lg-3 col-md-6">
                                <label className="form-label small mb-1">Reason</label>
                                <select
                                  className="form-select form-select-sm"
                                  aria-label="Reason category"
                                  value={draftReasonCategory}
                                  onChange={(e) => setDraftReasonCategory(e.target.value)}
                                >
                                  <option value="">No category</option>
                                  {reasonOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.value}
                                    </option>
                                  ))}
                                  {draftReasonCategory &&
                                    !reasonOptions.some((option) => option.value === draftReasonCategory) && (
                                      <option value={draftReasonCategory}>{draftReasonCategory}</option>
                                    )}
                                </select>
                              </div>
                              <div className="col-xl-5 col-lg-5 col-md-6">
                                <label className="form-label small mb-1" htmlFor={inputId}>
                                  Note
                                </label>
                                <input
                                  id={inputId}
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="e.g. Covers Q3 estimated taxes"
                                  value={draftNote}
                                  onChange={(e) => setDraftNote(e.target.value)}
                                />
                              </div>
                              <div className="col-xl-3 col-lg-4 col-md-7">
                                <label className="form-label small mb-1">Reserve Link</label>
                                <select
                                  className="form-select form-select-sm"
                                  style={{ minWidth: '14rem' }}
                                  aria-label="Linked reserve"
                                  value={draftLinks[0]?.reserve_id || ''}
                                  onChange={(e) =>
                                    upsertPrimaryLink({
                                      reserve_id: e.target.value,
                                    })
                                  }
                                >
                                  <option value="">Select a reserve...</option>
                                  {reserveOptions.map((option) => (
                                    <option key={option.id} value={option.id}>
                                      {option.label}
                                    </option>
                                  ))}
                                  {draftLinks[0]?.reserve_id &&
                                    !reserveOptions.some((o) => o.id === draftLinks[0].reserve_id) && (
                                      <option value={draftLinks[0].reserve_id}>
                                        {draftLinks[0].label || draftLinks[0].reserve_id} (no longer configured)
                                      </option>
                                    )}
                                </select>
                              </div>
                              <div className="col-xl-2 col-lg-12 col-md-5">
                                <label className="form-label small mb-1">Amount</label>
                                <div className="input-group input-group-sm">
                                  <span className="input-group-text">$</span>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    className="form-control"
                                    placeholder="Opt."
                                    aria-label="Allocated amount"
                                    value={draftLinks[0]?.allocated_amount || ''}
                                    onChange={(e) =>
                                      upsertPrimaryLink({
                                        allocated_amount: e.target.value,
                                      })
                                    }
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger"
                                    title="Remove this link"
                                    onClick={() => removeDraftLink(0)}
                                    disabled={draftLinks.length === 0}
                                  >
                                    <i className="bi bi-x-lg"></i>
                                  </button>
                                </div>
                              </div>
                            </div>

                          </div>

                          <div className="mt-0">
                            {/* Additional reserve links (beyond the primary row link). */}
                            {draftLinks.slice(1).map((link, index) => (
                              <div key={link._key || index} className="row g-2 align-items-end mt-1">
                                <div className="col-xl-7 col-lg-8 col-md-12 text-muted small">
                                  {index === 0 &&
                                    (selectedReasonOption
                                      ? `Examples: ${selectedReasonOption.examples.join('; ')}`
                                      : inferTransactionFlow(tx) === 'outflow'
                                      ? 'Examples: One-time cash need; monthly living expenses; tax payment'
                                      : inferTransactionFlow(tx) === 'inflow'
                                      ? 'Examples: Regular savings; annual contribution; rollover'
                                      : 'Examples: Select the option that best fits, or leave blank')}
                                </div>
                                <div className="col-xl-3 col-lg-4 col-md-7">
                                  <select
                                    className="form-select form-select-sm"
                                    style={{ minWidth: '14rem' }}
                                    aria-label="Linked reserve"
                                    value={link.reserve_id}
                                    onChange={(e) => updateDraftLink(index + 1, { reserve_id: e.target.value })}
                                  >
                                    <option value="">Select a reserve...</option>
                                    {reserveOptions.map((option) => (
                                      <option key={option.id} value={option.id}>
                                        {option.label}
                                      </option>
                                    ))}
                                    {/* Keep a stale link selectable if its reserve was removed. */}
                                    {link.reserve_id && !reserveOptions.some((o) => o.id === link.reserve_id) && (
                                      <option value={link.reserve_id}>
                                        {link.label || link.reserve_id} (no longer configured)
                                      </option>
                                    )}
                                  </select>
                                </div>
                                <div className="col-xl-2 col-lg-12 col-md-5">
                                  <div className="d-flex align-items-center gap-2">
                                    <div className="input-group input-group-sm">
                                      <span className="input-group-text">$</span>
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        className="form-control"
                                        placeholder="Amount (opt.)"
                                        aria-label="Allocated amount"
                                        value={link.allocated_amount}
                                        onChange={(e) =>
                                          updateDraftLink(index + 1, { allocated_amount: e.target.value })
                                        }
                                      />
                                    </div>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger flex-shrink-0"
                                      onClick={() => removeDraftLink(index + 1)}
                                      title="Remove this link"
                                    >
                                      <i className="bi bi-x-lg"></i>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}

                            <div className="row g-2 mt-1">
                              <div className="col-xl-7 col-lg-8 col-md-12 text-muted small">
                                {draftLinks.length <= 1 &&
                                  (selectedReasonOption
                                    ? `Examples: ${selectedReasonOption.examples.join('; ')}`
                                    : inferTransactionFlow(tx) === 'outflow'
                                    ? 'Examples: One-time cash need; monthly living expenses; tax payment'
                                    : inferTransactionFlow(tx) === 'inflow'
                                    ? 'Examples: Regular savings; annual contribution; rollover'
                                    : 'Examples: Select the option that best fits, or leave blank')}
                              </div>
                              <div className="col-xl-5 col-lg-4 col-md-12 d-flex justify-content-end">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link p-0 text-decoration-none"
                                  onClick={addDraftLink}
                                >
                                  <i className="bi bi-plus-circle me-1"></i>
                                  Add reserve link
                                </button>
                              </div>
                            </div>

                            <div className="d-flex align-items-center justify-content-end gap-2 mt-2">
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => saveNote(row)}
                                disabled={savingNote}
                              >
                                <i className={`bi ${savingNote ? 'bi-hourglass-split' : 'bi-check-lg'} me-1`}></i>
                                {savingNote ? 'Saving…' : 'Save'}
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={closeEditor}
                                disabled={savingNote}
                              >
                                Cancel
                              </button>
                              {noteError && <span className="text-danger small">{noteError}</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TransactionsPanel;
